import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib/utils';
import type { SidebarItem } from '../model/sidebar.types';
import { SidebarHeader } from './sidebar-header';
import { SidebarContent } from './sidebar-content';
import { SidebarFooter } from './sidebar-footer';

interface SidebarProps {
  className?: string;
  items: SidebarItem[];
  footerItems?: SidebarItem[];
  onItemClick?: (item: SidebarItem) => void;
  headerContent?: React.ReactNode;
  footerContent?: React.ReactNode;
  defaultCollapsed?: boolean;
}

export function Sidebar({
  className,
  items,
  footerItems,
  onItemClick,
  headerContent,
  footerContent,
  defaultCollapsed,
}: SidebarProps): React.ReactElement {
  const { t } = useTranslation('cabinet');
  const [collapsed, setCollapsed] = useState(defaultCollapsed || false);

  const handleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const collapseItem: SidebarItem = {
    id: 'collapse',
    label: t('sidebar.collapse'),
    icon: 'angles-left',
    href: '#',
    active: false,
  };

  return (
    <aside
      className={cn(
        'flex h-full w-sidebar shrink-0 flex-col overflow-hidden bg-background',
        'transition-[width] duration-slow ease-standard',
        collapsed && 'w-sidebar-collapsed',
        className
      )}
      data-name="Sidebar"
    >
      <SidebarHeader collapsed={collapsed}>{headerContent}</SidebarHeader>

      <SidebarContent
        items={items}
        onItemClick={onItemClick}
        collapsed={collapsed}
      />

      <SidebarFooter
        items={footerItems ?? [collapseItem]}
        onItemClick={(item) => {
          if (item.id === 'collapse') {
            handleCollapse();
            return;
          }
          onItemClick?.(item);
        }}
        collapsed={collapsed}
      >
        {footerContent}
      </SidebarFooter>
    </aside>
  );
}
